// Top-level row sort applied after grouping + filtering. Children
// inside a ParentRow keep their natural occurrence-date order — the
// sort only re-orders the schedule-level rows that the user paginates.
//
// Columns: name / owner / due / status / rag. Status sorts on the
// derived value (overdue surfaces); rag sorts on effective rag
// (overdue → red).

import { deriveDeliverableStatus } from "./deriveDeliverableStatus";
import { deriveEffectiveRag } from "./deriveEffectiveRag";
import type { Row, ParentRow, OneOffRow } from "./groupDeliverables";
import type { Deliverable } from "../../../../types";

export type SortableColumn = "name" | "owner" | "due" | "status" | "rag";
export type SortDirection = "ascending" | "descending";

// Rejected sorts above submitted/in-progress because a rejected item
// is a known failure that needs rework, more
// attention-worthy than a fresh submission awaiting review. `late`
// (delayed delivery awaiting review) sits between overdue and
// revision-requested — past-deadline but not blocking the supplier.
const STATUS_ORDER: Record<string, number> = {
  overdue: 0,
  late: 1,
  "revision-requested": 2,
  rejected: 3,
  "not-started": 4,
  submitted: 5,
  "under-review": 6,
  accepted: 7,
  closed: 8,
};

const RAG_ORDER: Record<string, number> = {
  red: 0,
  amber: 1,
  green: 2,
  unset: 3,
};

function representativeDeliverable(row: Row): Deliverable {
  if (row.kind === "parent") return row.deliverable;
  return row.deliverable;
}

const TERMINAL_FOR_DUE_SORT = new Set([
  "accepted",
  "rejected",
  "closed",
]);

// C1: parent rows have no native dueDate — they're a schedule, not an
// occurrence. The Due-column sort key is the EARLIEST non-terminal
// child's dueDate. Terminal-status children (accepted / rejected /
// closed) are historical and must NOT define the parent's sort
// position — otherwise an old accepted child could float a live
// schedule to the top of an ascending sort. Parents with no
// non-terminal children sort to the end via the "￿" sentinel.
function representativeDueDate(row: Row): string {
  if (row.kind === "parent") {
    let earliest: string | undefined;
    for (const c of row.children) {
      const due = c.deliverable.dueDate;
      if (!due) continue;
      if (TERMINAL_FOR_DUE_SORT.has(c.deliverable.status)) continue;
      if (earliest === undefined || due < earliest) earliest = due;
    }
    return earliest ?? "￿";
  }
  return row.deliverable.dueDate ?? "￿";
}

function compare(a: Row, b: Row, column: SortableColumn): number {
  const da = representativeDeliverable(a);
  const db = representativeDeliverable(b);
  switch (column) {
    case "name":
      return da.name.localeCompare(db.name);
    case "owner":
      return da.owner.localeCompare(db.owner);
    case "due": {
      const ax = representativeDueDate(a);
      const bx = representativeDueDate(b);
      return ax.localeCompare(bx);
    }
    case "status": {
      const sa =
        STATUS_ORDER[
          deriveDeliverableStatus(
            da.status,
            da.dueDate,
            new Date(),
            da.submittedAt,
          )
        ] ?? 99;
      const sb =
        STATUS_ORDER[
          deriveDeliverableStatus(
            db.status,
            db.dueDate,
            new Date(),
            db.submittedAt,
          )
        ] ?? 99;
      return sa - sb;
    }
    case "rag": {
      const ra = RAG_ORDER[deriveEffectiveRag(da) ?? "unset"] ?? 99;
      const rb = RAG_ORDER[deriveEffectiveRag(db) ?? "unset"] ?? 99;
      return ra - rb;
    }
    default:
      return 0;
  }
}

export function sortRows(
  rows: Row[],
  column: SortableColumn | null,
  direction: SortDirection | null,
): Row[] {
  if (!column || !direction) return rows;
  const sorted = [...rows].sort((a, b) => {
    const cmp = compare(a, b, column);
    if (cmp !== 0) return direction === "ascending" ? cmp : -cmp;
    // Stable tie-break on id so output is deterministic.
    return a.deliverable.id < b.deliverable.id ? -1 : 1;
  });
  return sorted;
}

// Sanity export used by integration tests asserting type narrowing on
// ParentRow / OneOffRow when sorting.
export type { ParentRow, OneOffRow };
